import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const commercialPosts = [
  {
    slug: 'best-security-system-small-business-ontario',
    title: 'Best Security System for Small Business in Ontario: The Complete 2025 Guide',
    excerpt:
      'Running a small business in Ontario? This complete guide covers the best commercial security systems, what they cost, which features matter most, and how to choose the right setup for your specific industry.',
    date: 'January 15, 2025',
    readTime: 14,
    category: 'Buying Guide',
    categoryColor: 'bg-amber-100 text-amber-700',
    tags: ['Small Business', 'Ontario', 'Buying Guide'],
    image:
      'https://public.readdy.ai/ai/img_res/a8e796d8d53e8c79b6a4925d2f774ce7.jpg',
    featured: true,
    icon: 'ri-store-2-line',
    highlight: 'Most Popular',
  },
  {
    slug: 'commercial-security-camera-system-toronto',
    title: "Commercial Security Camera Systems in Toronto: The Complete Buyer's Guide for 2025",
    excerpt:
      'Choosing a commercial security camera system in Toronto? This in-depth guide covers the best camera types, NVR systems, installation costs, and how to design a surveillance layout for your business.',
    date: 'February 5, 2025',
    readTime: 13,
    category: 'Cameras',
    categoryColor: 'bg-sky-100 text-sky-700',
    tags: ['Toronto', 'Security Cameras', 'CCTV'],
    image:
      'https://readdy.ai/api/search-image?query=modern%20Toronto%20commercial%20building%20exterior%20at%20dusk%20with%20professional%204K%20security%20cameras%20mounted%20on%20facade%20urban%20downtown%20skyline%20background%20professional%20corporate%20photography&width=800&height=500&seq=blog11&orientation=landscape',
    featured: true,
    icon: 'ri-camera-line',
    highlight: 'Featured',
  },
  {
    slug: 'access-control-small-business-ontario',
    title: 'Access Control for Small Business in Ontario: Everything You Need to Know in 2025',
    excerpt:
      'Access control is no longer just for large corporations. This guide explains the best access control systems for Ontario small businesses, costs, integrations, and which system fits your industry.',
    date: 'February 20, 2025',
    readTime: 11,
    category: 'Access Control',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    tags: ['Access Control', 'Small Business', 'Ontario'],
    image:
      'https://readdy.ai/api/search-image?query=modern%20office%20entrance%20with%20sleek%20keycard%20access%20control%20panel%20mounted%20beside%20glass%20door%20professional%20corporate%20building%20interior%20clean%20minimalist%20design%20Ontario%20Canada&width=800&height=500&seq=blog12&orientation=landscape',
    featured: false,
    icon: 'ri-key-2-line',
    highlight: null,
  },
  {
    slug: 'commercial-alarm-monitoring-gta-guide',
    title: 'Commercial Alarm Monitoring in the GTA: How It Works and Why Your Business Needs It',
    excerpt:
      'Professional alarm monitoring is the backbone of any serious commercial security system. Learn how ULC-certified monitoring works, what to look for in a provider, and why self-monitoring is risky.',
    date: 'March 3, 2025',
    readTime: 10,
    category: 'Monitoring',
    categoryColor: 'bg-rose-100 text-rose-700',
    tags: ['Alarm Monitoring', 'GTA', 'ULC'],
    image:
      'https://readdy.ai/api/search-image?query=professional%20security%20monitoring%20centre%20interior%20with%20multiple%20screens%20showing%20live%20camera%20feeds%20operators%20at%20workstations%20modern%20command%20centre%20low%20blue%20ambient%20lighting&width=800&height=500&seq=blog13&orientation=landscape',
    featured: false,
    icon: 'ri-live-line',
    highlight: null,
  },
];

const stats = [
  { value: '500+', label: 'Commercial Clients', icon: 'ri-building-2-line' },
  { value: '$8,400', label: 'Avg. Break-in Loss', icon: 'ri-money-dollar-circle-line' },
  { value: '20%', label: 'Insurance Discount', icon: 'ri-shield-check-line' },
  { value: '24/7', label: 'Live Monitoring', icon: 'ri-time-line' },
];

const topicCards = [
  {
    icon: 'ri-camera-line',
    title: 'Commercial Cameras',
    desc: '4K surveillance, AI detection, multi-site management',
    link: '/camera-packages',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Business Alarms',
    desc: 'Cellular backup, silent panic, instant dispatch',
    link: '/alarm-packages',
  },
  {
    icon: 'ri-key-2-line',
    title: 'Access Control',
    desc: 'Card, fob, biometric & mobile credentials',
    link: '/access-control',
  },
  {
    icon: 'ri-live-line',
    title: 'Live Monitoring',
    desc: '24/7 trained operators, audio talk-down',
    link: '/live-monitoring',
  },
];

/* -------------------------------------------------------------------------
   FAQItem – a tiny component that owns its own open/close state.
   Extracting it out of the map callback removes the illegal hook call
   inside a loop and fixes the compile error.
-------------------------------------------------------------------------- */
function FAQItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#F8F9FA] rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
      >
        <span className="font-semibold text-[#0A1628] pr-4 text-sm">{question}</span>
        <i
          className={`ri-arrow-down-s-line text-xl text-[#D4A853] transition-transform flex-shrink-0 ${
            open ? 'rotate-180' : ''
          }`}
        ></i>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Main Page Component
-------------------------------------------------------------------------- */
export default function CommercialSecurityHubPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);

  const industryOptions = [
    'Retail & Restaurants',
    'Office Buildings',
    'Warehouses & Industrial',
    'Healthcare & Medical',
    'Hotels & Hospitality',
    'Construction Sites',
    'Car Dealerships',
    'Cannabis Facilities',
    'Multi-Family Residential',
    'Other',
  ];

  const scrollToForm = () => {
    const el = document.getElementById('commercial-hub-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }
    setFormStatus('submitting');
    const formData = new FormData(form);
    formData.set('industry', selectedIndustry);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => urlEncoded.append(key, value.toString()));
    try {
      const response = await fetch('https://readdy.ai/api/form/d6ggiplflgn4c71tl1u0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedIndustry('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Commercial Security Resource Hub — Alliance Security Systems',
    description:
      'Expert guides on commercial security systems in Ontario: cameras, alarms, access control, and alarm monitoring for small businesses across the GTA.',
    url: 'https://alliancesecurity.ca/commercial-security',
    publisher: {
      '@type': 'Organization',
      name: 'Alliance Security Systems',
      telephone: '1-888-458-9181',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: commercialPosts.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://alliancesecurity.ca/blog/${p.slug}`,
        name: p.title,
      })),
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is the best commercial security system for a small business in Ontario?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For most Ontario small businesses, the best commercial security system combines HD cameras at entry points and POS areas, a ULC-monitored alarm with cellular backup, and basic access control. A 4-camera system with monitoring typically starts at $1,500–$3,000 installed, with monitoring from $29.99/month.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does commercial security cost in the GTA?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Commercial security in the GTA typically costs $2,500–$6,000 for a small business (4–8 cameras + alarm), $6,000–$15,000 for mid-size (8–16 cameras + access control), and $15,000+ for large industrial or multi-site deployments. Monthly monitoring ranges from $39.99 to $79.99.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does a commercial security system lower business insurance premiums?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Most Ontario commercial insurers offer 5–20% discounts for businesses with professionally monitored alarm systems and verified camera coverage. ULC-listed monitoring stations are often required. Alliance Security provides full documentation for insurance purposes.',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Commercial Security Systems Ontario | Cameras, Alarms & Access Control Guides | Alliance Security"
        description="Expert commercial security guides for Ontario businesses: best security cameras in Toronto, access control for small business, alarm monitoring in the GTA, and more. Free assessment — call 1-888-458-9181."
        keywords="commercial security Ontario, business security cameras Toronto, access control small business Ontario, alarm monitoring GTA, commercial security systems"
        canonical="/commercial-security"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>
                ALLIANCE
              </span>
              <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/business-security" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              Business Security
            </Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Cameras
            </Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Alarms
            </Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              Access Control
            </Link>
            <Link to="/live-monitoring" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              Live Monitoring
            </Link>
            <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Blog
            </Link>
            <Link to="/commercial-security/service-areas" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              Service Areas
            </Link>
            <a href="tel:18884589181" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
            <button onClick={scrollToForm} className="bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
              Free Assessment
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-1">
            <Link to="/" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Home
            </Link>
            <Link to="/business-security" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Business Security
            </Link>
            <Link to="/camera-packages" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Cameras
            </Link>
            <Link to="/alarm-packages" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Alarms
            </Link>
            <Link to="/access-control" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Access Control
            </Link>
            <Link to="/live-monitoring" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Live Monitoring
            </Link>
            <Link to="/blog" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Blog
            </Link>
            <Link to="/commercial-security/service-areas" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Service Areas
            </Link>
            <button onClick={scrollToForm} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">
              Free Assessment
            </button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20commercial%20business%20district%20Toronto%20skyline%20at%20golden%20hour%20glass%20office%20towers%20retail%20storefronts%20professional%20buildings%20warm%20dramatic%20lighting%20wide%20angle%20aerial%20view%20Ontario%20Canada&width=1920&height=1080&seq=commhub-hero1&orientation=landscape"
              alt="Commercial Security Ontario GTA"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/92 via-[#0A1628]/70 to-[#0A1628]/30"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-16">
            <nav className="flex items-center gap-2 text-xs text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#D4A853] cursor-pointer">
                Blog
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Commercial Security</span>
            </nav>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-5 whitespace-nowrap">
                <i className="ri-building-2-line"></i>
                COMMERCIAL SECURITY RESOURCE HUB
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
                Commercial Security Guides for Ontario Businesses
              </h1>
              <p className="text-lg sm:text-xl text-white/85 mb-8 leading-relaxed max-w-2xl">
                Expert-written guides on cameras, alarms, access control, and monitoring — everything you need to protect your Ontario business in 2025.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={scrollToForm}
                  className="bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center justify-center gap-3"
                >
                  Get Free Business Assessment
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </button>
                <a
                  href="tel:18884589181"
                  className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line"></i>1-888-458-9181
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#0A1628] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-9 h-9 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className={`${s.icon} text-base text-[#D4A853]`}></i>
                  </div>
                  <p className="text-2xl font-bold text-[#D4A853]">{s.value}</p>
                  <p className="text-xs text-white/60 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Topic Quick Links */}
        <section className="py-12 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-2">Explore by Topic</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628]">Commercial Security Solutions</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {topicCards.map((t, i) => (
                <Link
                  key={i}
                  to={t.link}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group text-center"
                >
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-[#D4A853] transition-colors">
                    <i className={`${t.icon} text-2xl text-[#D4A853] group-hover:text-white transition-colors`}></i>
                  </div>
                  <h3 className="font-bold text-[#0A1628] text-sm mb-1">{t.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{t.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block bg-[#D4A853] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                Featured Guide
              </span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Featured card — first post */}
            <Link
              to={`/blog/${commercialPosts[0].slug}`}
              className="group block bg-[#F8F9FA] rounded-3xl overflow-hidden border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer mb-12"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative w-full h-64 lg:h-auto min-h-[320px]">
                  <img
                    src={commercialPosts[0].image}
                    alt={commercialPosts[0].title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F8F9FA]/20 lg:block hidden"></div>
                  <span className="absolute top-4 left-4 bg-[#D4A853] text-white px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap">
                    ⭐ Most Popular
                  </span>
                </div>
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-4 w-fit ${commercialPosts[0].categoryColor}`}>
                    {commercialPosts[0].category}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A1628] mb-4 leading-tight group-hover:text-[#D4A853] transition-colors">
                    {commercialPosts[0].title}
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">{commercialPosts[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-6">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {commercialPosts[0].date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {commercialPosts[0].readTime} min read
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {commercialPosts[0].tags.map((tag) => (
                      <span key={tag} className="bg-[#0A1628]/5 text-[#0A1628] px-3 py-1 rounded-full text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>

            {/* Remaining posts */}
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block bg-[#0A1628] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                All Guides
              </span>
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400">{commercialPosts.length} articles</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {commercialPosts.slice(1).map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    {post.highlight && (
                      <span className="absolute top-3 left-3 bg-[#D4A853] text-white px-3 py-1 rounded-full text-[11px] font-bold whitespace-nowrap">
                        {post.highlight}
                      </span>
                    )}
                    <div className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow-sm">
                      <i className={`${post.icon} text-[#D4A853] text-base`}></i>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className={`inline-block text-[11px] font-bold px-2.5 py-1 rounded-full mb-3 w-fit ${post.categoryColor}`}>
                      {post.category}
                    </span>
                    <h3 className="font-extrabold text-[#0A1628] text-base leading-snug mb-3 group-hover:text-[#D4A853] transition-colors flex-1">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400 pt-4 border-t border-gray-100">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime} min read
                      </span>
                      <span className="flex items-center gap-1 text-[#D4A853] font-semibold group-hover:gap-2 transition-all">
                        Read <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}

              {/* CTA card */}
              <div
                onClick={scrollToForm}
                className="bg-gradient-to-br from-[#0A1628] to-[#1a2d4a] rounded-2xl p-8 flex flex-col justify-between cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-[#D4A853]/20"
              >
                <div>
                  <div className="w-12 h-12 bg-[#D4A853] rounded-xl flex items-center justify-center mb-5">
                    <i className="ri-customer-service-2-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-extrabold text-white mb-3 leading-snug">
                    Ready to Secure Your Business?
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-6">
                    Get a free, no-obligation commercial security assessment from our certified specialists. We respond within 2 hours.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-colors whitespace-nowrap w-fit">
                  Get Free Assessment <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Read These Guides */}
        <section className="py-16 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  WHY THESE GUIDES
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-5 leading-tight">
                  Written by Certified Commercial Security Specialists
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  Every guide in this hub is written by Alliance Security's certified commercial security team — professionals who have designed and installed systems for 500+ Ontario businesses since 1999. No fluff, no affiliate bias — just practical, experience-backed advice.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-verified-badge-line', title: 'Factory-Certified Authors', desc: 'Certified by Honeywell, DSC, Hikvision, Kantech & more' },
                    { icon: 'ri-map-pin-line', title: 'GTA-Specific Advice', desc: 'Pricing, regulations, and recommendations specific to Ontario' },
                    { icon: 'ri-refresh-line', title: 'Updated for 2025', desc: 'All guides reflect current products, pricing, and best practices' },
                    { icon: 'ri-building-2-line', title: '500+ Commercial Installs', desc: 'Real-world experience across every industry and business size' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#0A1628] text-sm">{item.title}</p>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative w-full h-[380px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20security%20consultant%20team%20meeting%20in%20modern%20office%20reviewing%20commercial%20security%20system%20plans%20blueprints%20on%20table%20confident%20experts%20business%20attire%20bright%20clean%20workspace%20Ontario%20Canada&width=800&height=600&seq=commhub-team1&orientation=landscape"
                  alt="Alliance Security Commercial Team"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-5 left-5 right-5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                  <p className="text-white font-bold text-sm">Alliance Security Systems</p>
                  <p className="text-white/70 text-xs">Serving the GTA since 1999 · A+ BBB Rating · 30,000+ Installations</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                QUICK ANSWERS
              </span>
              <h2 className="text-3xl font-bold text-[#0A1628]">Commercial Security FAQs</h2>
            </div>
            <div className="space-y-3">
              {[
                {
                  q: 'What is the best commercial security system for a small business in Ontario?',
                  a: 'For most Ontario small businesses, the best setup combines HD cameras at entry points and POS areas, a ULC‑monitored alarm with cellular backup, and basic access control. A 4‑camera system with monitoring typically starts at $1,500–$3,000 installed, with monitoring from $29.99/month.',
                },
                {
                  q: 'How much does commercial security cost in the GTA?',
                  a: 'Small business (4–8 cameras + alarm): $2,500–$6,000. Mid‑size (8–16 cameras + access control): $6,000–$15,000. Large industrial or multi‑site: $15,000+. Monthly monitoring ranges from $39.99 to $79.99 depending on services.',
                },
                {
                  q: 'Does a commercial security system lower my business insurance?',
                  a: 'Yes. Most Ontario commercial insurers offer 5–20% discounts for businesses with professionally monitored alarm systems and verified camera coverage. ULC‑listed monitoring is often required. We provide full documentation for insurance purposes.',
                },
                {
                  q: 'Can I manage multiple business locations from one system?',
                  a: 'Absolutely. Our enterprise platform lets you monitor all locations from a single app — view live feeds, review footage, manage access credentials, and receive alerts across every site simultaneously.',
                },
              ].map((faq, i) => (
                <FAQItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Lead Capture Form */}
        <section id="commercial-hub-form" className="py-16 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left */}
              <div>
                <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-5 whitespace-nowrap">
                  FREE COMMERCIAL ASSESSMENT
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 leading-tight">
                  Get a Free Commercial Security Assessment
                </h2>
                <p className="text-white/75 text-base leading-relaxed mb-8">
                  Tell us about your business and our certified commercial security consultants will design a custom solution — completely free, no obligation. We respond within 2 business hours.
                </p>
                <div className="space-y-5 mb-8">
                  {[
                    { icon: 'ri-timer-line', title: '2-Hour Response', desc: 'Our team responds to all inquiries within 2 business hours' },
                    { icon: 'ri-map-pin-line', title: 'Free On-Site Visit', desc: 'We come to your business for a thorough security assessment' },
                    { icon: 'ri-file-text-line', title: 'Itemized Proposal', desc: 'Transparent quote with no hidden fees or surprise charges' },
                    { icon: 'ri-shield-check-line', title: 'Zero Obligation', desc: 'The assessment and quote are completely free — no pressure' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-lg text-white"></i>
                      </div>
                      <div>
                        <p className="text-white/50 text-xs mb-0.5">Prefer to speak now?</p>
                        <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                          1-888-458-9181
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — Form */}
              <div className="bg-white rounded-3xl p-7 sm:p-10 shadow-2xl">
                {formStatus === 'success' ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <i className="ri-check-line text-4xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Request Received!</h3>
                    <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
                      Our commercial security specialists will contact you within 2 business hours to schedule your free on‑site assessment.
                    </p>
                    <button onClick={() => setFormStatus('idle')} className="text-[#D4A853] font-semibold hover:underline cursor-pointer text-sm whitespace-nowrap">
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-1">Request Your Free Assessment</h3>
                    <p className="text-sm text-gray-400 mb-7">Fill out the form and we'll be in touch shortly.</p>
                    <form
                      id="commercial-security-hub-form"
                      data-readdy-form
                      onSubmit={handleFormSubmit}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(416) 555-0123"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                          Business Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="john@yourbusiness.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                          Business Name *
                        </label>
                        <input
                          type="text"
                          name="businessName"
                          required
                          placeholder="Your Business Inc."
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>
                      <div className="relative">
                        <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                          Industry / Business Type *
                        </label>
                        <input type="hidden" name="industry" value={selectedIndustry} />
                        <button
                          type="button"
                          onClick={() => setIndustryDropdownOpen(!industryDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all cursor-pointer"
                        >
                          <span className={selectedIndustry ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedIndustry || 'Select your industry'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${industryDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {industryDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-52 overflow-y-auto">
                            {industryOptions.map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                  setSelectedIndustry(opt);
                                  setIndustryDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${
                                  selectedIndustry === opt
                                    ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5'
                                    : 'text-gray-700'
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                            Number of Locations
                          </label>
                          <input
                            type="text"
                            name="locations"
                            placeholder="e.g. 1, 3, 10+"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                            City / Area
                          </label>
                          <input
                            type="text"
                            name="city"
                            placeholder="e.g. Toronto, Mississauga"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0A1628] mb-1.5">
                          Security Needs & Details
                        </label>
                        <textarea
                          name="details"
                          rows={3}
                          maxLength={500}
                          placeholder="Describe your current setup, main concerns, and what you're looking for..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F8F9FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>
                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedIndustry}
                        className="w-full bg-[#D4A853] text-white py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Commercial Assessment <i className="ri-arrow-right-line text-lg"></i>
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our{' '}
                        <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">
                          Privacy Policy
                        </Link>
                        . We never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Related Pages CTA strip */}
        <section className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Ready to go beyond the guides?</h3>
                <p className="text-white/85 text-sm">Explore our full commercial security solutions or view all blog articles.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/business-security"
                  className="bg-white text-[#D4A853] px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-lg"
                >
                  Business Security Page
                </Link>
                <Link
                  to="/blog"
                  className="border-2 border-white text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-white hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
                >
                  All Blog Articles
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Security by City */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                LOCAL COMMERCIAL SECURITY
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
                Commercial Security by City
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                Find commercial security solutions, local case studies, and city-specific pricing for your GTA location.
              </p>
              <Link
                to="/commercial-security/service-areas"
                className="inline-flex items-center gap-2 text-[#D4A853] font-semibold hover:gap-3 transition-all cursor-pointer"
              >
                View All Service Areas <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/commercial-security/${city.slug}`}
                  className="group bg-[#F8F9FA] hover:bg-[#D4A853] rounded-xl p-6 text-center transition-all duration-300 border border-gray-100 hover:border-[#D4A853] hover:shadow-lg cursor-pointer"
                >
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-white/20 transition-colors">
                    <i className="ri-building-line text-2xl text-[#D4A853] group-hover:text-white transition-colors"></i>
                  </div>
                  <h3 className="font-bold text-[#0A1628] group-hover:text-white transition-colors text-sm">
                    {city.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  ALLIANCE
                </span>
                <span className="text-[8px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <div className="flex items-center gap-4 flex-wrap justify-center text-xs">
              <Link to="/" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Home
              </Link>
              <Link to="/business-security" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Business Security
              </Link>
              <Link to="/camera-packages" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Cameras
              </Link>
              <Link to="/alarm-packages" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Alarms
              </Link>
              <Link to="/access-control" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Access Control
              </Link>
              <Link to="/blog" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Blog
              </Link>
              <Link to="/commercial-security/service-areas" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Service Areas
              </Link>
              <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                Privacy Policy
              </Link>
            </div>
            <p className="text-white/40 text-xs">© 2025 Alliance Security Systems. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
